import React, { useState, useEffect, useRef } from "react";
import { Checkbox, AutoComplete, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { transformListForSelect, markAddedOptionsDisabled } from "@/utils/list";

import ExclusiveBadge from "@/components/blocks/ExclusiveBadge";
import AudioTrackSelector from "@/components/film/AudioTrackSelector";
import { storeUser } from "@/store/user";
import { filmStore } from "@/store/film";
import api from "@/api/index";

import "./styles/film-video-file.scss";

interface Props {
  index?: number;
  isEdit?: boolean;
  data?: any;
  update: (key: string, value: any) => void;
  remove?: (index: number) => void;
}

function FilmVideoFile({ remove, index, isEdit, data, update }: Props) {
  const [voiceOver, setVoiceover] = useState<string>("");
  const [voiceoverId, setVoiceoverId] = useState<number | null>(null);
  const [file, setFile] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAds, setIsAds] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true)
  const [exclusiveEnabled, setExclusiveEnabled] = useState<boolean>(false)
  const [exclusiveDays, setExclusiveDays] = useState<number>(0)
  const [maxAvailableDays, setMaxAvailableDays] = useState<number>(14)
  const [audioTrackIndex, setAudioTrackIndex] = useState<number | null>(null)
  const [deleteSource, setDeleteSource] = useState<boolean>(false)
  const exclusiveInitRef = useRef(false)

  // Вычисляет максимальное количество дней для эксклюзива при редактировании
  // на основе created_at (added_date на backend): max(0, 14 - daysSinceCreated)
  const calcMaxAvailableDays = (createdAt: string | null | undefined): number => {
    if (!createdAt) return 14;
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - createdDate.getTime();
    const daysPassed = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, 14 - daysPassed);
  };


  const [rawVoiceovers, setRawVoiceovers] = useState<Array<{ id: number; name: string }>>([]);
  const [voiceoverOptions, setVoiceoverOptions] = useState<Array<{ value: number; label: string }>>([]);

  const fetchFiles = async (search: string | null = null) => {
    const responseFiles = await api.files.get(null, search);
    const rawFilesAll = responseFiles.data.data.files || [];

    // Именно rawFilesAll: setState ещё не применился, и список оставался
    // пустым до следующего рендера (было причиной пустого селекта)
    const options = transformListForSelect(rawFilesAll, "relative_path", "relative_path");

    // Уже добавленные файлы показываем, но выбрать их нельзя — повторное
    // добавление отклоняет бэкенд. Значение value не меняется, поэтому
    // выбранный при редактировании файл по-прежнему отображается.
    // Исключение — файлы, занятые в текущей форме: из одного multi-audio файла
    // добавляется несколько озвучек, и он должен остаться выбираемым.
    const usedInForm = filmStore.listVoiceovers
      .map((item: any) => item?.video_file)
      .filter((path: any): path is string => typeof path === "string" && path.length > 0);

    setFiles(markAddedOptionsDisabled(options, usedInForm));
  };

  const fetchAll = async () => {
    try {
      setIsLoading(true);

      const responseVoiceover = await api.voiceovers.get();
      const rawVos = responseVoiceover.data.data.voiceovers || [];

      setRawVoiceovers(rawVos);
      setVoiceoverOptions(
        rawVos.map(item => ({ value: item.id, label: item.name }))
      );

      await fetchFiles();
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchOption = (text: string) => {
    if (!text.trim()) {
      setVoiceoverOptions(
          rawVoiceovers.map(item => ({ value: item.id, label: item.name }))
      );
    } else {
      const filtered = rawVoiceovers.filter(item =>
          item.name.toLowerCase().includes(text.toLowerCase())
      );
      setVoiceoverOptions(
          filtered.map(item => ({ value: item.id, label: item.name }))
      );
    }
  };

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Поиск файла идёт на сервере, а не по загруженному списку.
   *
   * Список обрезан лимитом (1000 записей): на проде в хранилище 3015 файлов,
   * и всё, что дальше лимита, в браузер не попадало — поиск по локальной копии
   * его не находил (так пропадал Tills.doden.skiljer.oss.S01...).
   * Сервер отбирает совпадения ДО применения лимита.
   */
  const handleSearchFile = (text: string) => {
    const term = text.trim();

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!term) {
      void fetchFiles();

      return;
    }

    searchTimerRef.current = setTimeout(() => {
      void fetchFiles(term);
    }, 300);
  }

  const onSelectName = (value: number, option: { value: number; label: string }) => {
    setVoiceover(option.label);
    setVoiceoverId(value);
    update("voiceover_id", value);
    update("voiceoverName", option.label);
  };

  const onSelectFile = (value) => {
    // Смена исходника обнуляет выбранную дорожку: индекс относится к конкретному
    // файлу. Сбрасываем только при реальной смене файла — иначе набор текста в
    // автокомплите стирал выбор на каждом символе (D-5).
    if (value !== file) {
      setAudioTrackIndex(null);
      update("audio_track_index", null);
    }

    setTimeout(() => {
      update("video_file_label", value);
    }, 1000)
  }

  const handleChangeFile = (val: string) => {
    setFile(val);
    update("video_file", val);
  };

  const handleTrackSelect = (index: number) => {
    setAudioTrackIndex(index);
    update("audio_track_index", index);
  };


  // Sync default isActive to parent on mount for new voiceovers
  useEffect(() => {
    if (!isEdit) {
      update("isActive", true);
    }
  }, []);

  useEffect(() => {
    if (isEdit && data) {
      const voiceoverId = data.voiceover_id || data.id;
      if (voiceoverId) {
        const found = rawVoiceovers.find(vo => vo.id === voiceoverId);
        if (found) {
          setVoiceover(found.name);
          setVoiceoverId(found.id);
        }
      }
      setIsAds(data.has_internal_ads || false);
      if (data.video_file?.relative_path) {
        setFile(data.video_file.relative_path);
      }

      if(data?.video_file?.status && !data.isChangeActive) {
        setIsActive(data?.active ?? (data?.video_file?.status === 'active'));
        update("isChangeActive", true);
      }
    }
  }, [isEdit, data, rawVoiceovers]);

  // Инициализация exclusive-состояния при редактировании — только один раз при открытии модалки
  useEffect(() => {
    if (isEdit && data && !exclusiveInitRef.current) {
      exclusiveInitRef.current = true;

      const maxDays = calcMaxAvailableDays(data.created_at);
      setMaxAvailableDays(maxDays);

      if (data.exclusive_until) {
        const until = new Date(data.exclusive_until);
        const now = new Date();
        const diffMs = until.getTime() - now.getTime();
        const remainingDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (remainingDays > 0 && maxDays > 0) {
          setExclusiveEnabled(true);
          const days = Math.min(remainingDays, maxDays);
          setExclusiveDays(days);
          update("exclusive_days", days);
        }
      }
    }
  }, [isEdit, data]);

  useEffect(() => {
    fetchAll();
  }, []);

  const [files, setFiles] = useState<any[]>([]);

  if (isLoading) {
    return (
        <div className="film-view__loader">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
    );
  }

  return (
      <div className="film-video-file">
        <div className={"form-group"}>
          <label className={"form-group-label"}>
            Название озвучки <span className={'app-required'}>*</span>
          </label>
          <AutoComplete
              options={voiceoverOptions}
              value={voiceOver}
              onSelect={onSelectName}
              onSearch={handleSearchOption}
              placeholder="Название озвучки"
              className={"app-select"}
              allowClear
              onChange={(value) => {
                setVoiceover(value);
              }}
          />
        </div>

        <div className={"form-group form-group__advert"}>
          <label className={"form-group-label"}>Встроенная реклама</label>
          <Checkbox
              checked={isAds}
              onChange={(e) => {
                setIsAds(e.target.checked);
                update("has_internal_ads", e.target.checked);
              }}
          />
        </div>

        {
          !isEdit && <div className={"form-group form-group__advert"} v-if={!isEdit}>
              <label className={"form-group-label"}>Активный</label>
              <Checkbox
                  checked={isActive}
                  onChange={(e) => {
                    setIsActive(e.target.checked);
                    update("isActive", e.target.checked);
                  }}
              />
            </div>
        }

        {
          !isEdit && (
            <div className={"form-group form-group__advert"}>
              <label className={"form-group-label"}>Эксклюзивный показ</label>
              <Checkbox
                  checked={exclusiveEnabled}
                  onChange={(e) => {
                    setExclusiveEnabled(e.target.checked);
                    if (!e.target.checked) {
                      setExclusiveDays(0);
                      update("exclusive_days", 0);
                    }
                  }}
              />
              {exclusiveEnabled && (
                <select
                  className="app-select exclusive-days-select"
                  value={exclusiveDays}
                  onChange={(e) => {
                    const days = Number(e.target.value);
                    setExclusiveDays(days);
                    update("exclusive_days", days);
                  }}
                >
                  {Array.from({ length: 14 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>{day} {day === 1 ? 'день' : day < 5 ? 'дня' : 'дней'}</option>
                  ))}
                </select>
              )}
            </div>
          )
        }

        {
          isEdit && maxAvailableDays > 0 && (
            <div className={"form-group form-group__advert"}>
              <label className={"form-group-label"}>Эксклюзивный показ</label>
              <Checkbox
                  checked={exclusiveEnabled}
                  disabled={false}
                  onChange={(e) => {
                    setExclusiveEnabled(e.target.checked);
                    if (!e.target.checked) {
                      setExclusiveDays(0);
                      update("exclusive_days", 0);
                    }
                  }}
              />
              {exclusiveEnabled && (
                <select
                  className="app-select exclusive-days-select"
                  value={exclusiveDays}
                  onChange={(e) => {
                    const days = Number(e.target.value);
                    setExclusiveDays(days);
                    update("exclusive_days", days);
                  }}
                >
                  {Array.from({ length: maxAvailableDays }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>{day} {day === 1 ? 'день' : day < 5 ? 'дня' : 'дней'}</option>
                  ))}
                </select>
              )}
            </div>
          )
        }

        {
          isEdit && maxAvailableDays === 0 && data?.exclusive_until && (
            <div className="form-group form-group__advert">
              <label className="form-group-label">Эксклюзивный показ:</label>
              <ExclusiveBadge exclusiveUntil={data.exclusive_until} />
            </div>
          )
        }

        {
          isEdit && maxAvailableDays === 0 && !data?.exclusive_until && (
            <div className="form-group form-group__advert">
              <label className="form-group-label">Эксклюзивный показ</label>
              <Checkbox
                  checked={false}
                  disabled={true}
              />
              <span className="exclusive-days-hint">доступен только в течение 14 дней с даты создания</span>
            </div>
          )
        }

        {!isEdit && (
            <div className={"form-group"}>
              <label className={"form-group-label"}>
                Выберите файл <span className={'app-required'}>*</span>
              </label>
              <AutoComplete
                  options={files}
                  value={file}
                  onSelect={onSelectFile}
                  onSearch={handleSearchFile}
                  placeholder="Выберите из списка"
                  className={"app-select"}
                  allowClear
                  onChange={(value) => {
                    handleChangeFile(value);
                  }}
              />
              {storeUser.isSuperAdmin && (
              <AudioTrackSelector
                filePath={file}
                selectedTrackIndex={audioTrackIndex}
                onTrackSelect={handleTrackSelect}
                disabled={isEdit}
              />
              )}
              {storeUser.isSuperAdmin && audioTrackIndex !== null && (
              <div style={{ marginTop: 8 }}>
                <Checkbox
                  checked={deleteSource}
                  onChange={(e) => {
                    setDeleteSource(e.target.checked);
                    update("delete_source", e.target.checked);
                  }}
                >
                  Удалить исходный файл после создания
                </Checkbox>
              </div>
              )}
            </div>
        )}
      </div>
  );
}

export default FilmVideoFile;